import {
  RULES_MUL_DIV_GPR,
  RULES_MUL_DIV_ROUNDS,
  RULES_MUL_DIV_START_SECONDS,
} from '../../games/data/vifRulesMulDivData';

import type { RulesMulDivFlag } from './rulesMulDivProblems';
import type { RulesVariantConfig } from './rulesVariantConfig';

/**
 * Folds a division flag back onto the multiplication range it shares a
 * `GPR`/`rounds` table slot with. Ported verbatim from
 * `VIFRulesMulDiv.computeBaseGameFlag` - division flags are always the
 * multiplication flag + 10 (e.g. flag 15's base is flag 5's slot).
 */
export function mulDivBaseGameFlag(flag: RulesMulDivFlag): number {
  return flag > 9 ? flag - 10 : flag;
}

/** Legacy `intLayersInGame` per flag, from `initGameFlag`'s switch. */
const LAYERS_IN_GAME: Record<RulesMulDivFlag, number> = {
  0: 2,
  1: 4,
  2: 2,
  3: 4,
  4: 4,
  5: 4,
  6: 4,
  7: 4,
  8: 4,
  10: 2,
  11: 4,
  12: 2,
  13: 4,
  14: 4,
  15: 4,
  16: 4,
};

/** Legacy `scaleVar = 2`, set for these flags in `initGameFlag`. */
const LARGE_ANSWERS: ReadonlySet<number> = new Set([
  1, 2, 3, 4, 5, 11, 12, 13, 14,
]);

/**
 * Legacy `baseGameFlag == 5`: flags 5 and 15. `checkForEndBody` ends their
 * game after a single cleared board, and `useEarlyEnableStartButton` sends a
 * wrong answer back to START instead of redealing
 * (`games/VIFRulesMulDiv.ts`, `games/engine/rulesBoardEngine.ts:74-85`).
 */
const BASE_FLAG_5: ReadonlySet<number> = new Set([5, 15]);

/**
 * Legacy `preString = 'x = '`, set only for flag 12 ("Divide Loose Mirror
 * Rule") - the same `'P'`-delimited stacked-fraction trick AddSub's flag 8
 * uses, reused here for a different flag number.
 */
const X_EQUALS_FLAG: RulesMulDivFlag = 12;

/** Flags 6/7/8: "Auditory Memory" (`VIFRulesMulDiv.ts`'s `gameFlag` 6/7/8). */
const AUDITORY_MEMORY_FLAGS: ReadonlySet<number> = new Set([6, 7, 8]);

export function mulDivVariantConfig(flag: RulesMulDivFlag): RulesVariantConfig {
  const baseFlag = mulDivBaseGameFlag(flag);

  return {
    layersInGame: LAYERS_IN_GAME[flag],
    // `RULES_MUL_DIV_GPR`/`RULES_MUL_DIV_ROUNDS` are indexed by the *base*
    // flag (division flags share multiplication's slot); `startSeconds`
    // below is indexed by the *raw* flag - the data file's object keys are
    // `flag0`..`flag16`, one per real flag, not folded.
    roundsInModule: RULES_MUL_DIV_ROUNDS[baseFlag]!,
    gamesPerRound: RULES_MUL_DIV_GPR[baseFlag]!,
    startSeconds: RULES_MUL_DIV_START_SECONDS[`flag${flag}`],
    large: LARGE_ANSWERS.has(flag),
    endsAfterOneBoard: BASE_FLAG_5.has(flag),
    rejectReturnsToStart: BASE_FLAG_5.has(flag),
    preString: flag === X_EQUALS_FLAG ? 'x = ' : '',
    spokenEquations: AUDITORY_MEMORY_FLAGS.has(flag),
  };
}
